/** @fileoverview Calls Core-proxied Fabrik app datasource APIs. */

import { isRecord } from '../lib/utils';
import { effectiveDateQueryFields } from '../components/AppRenderer/effectiveDates';
import { buildWorkdayQueryBody } from '../components/AppRenderer/workdaySource';
import {
  getBuilderFieldOptionsPath,
  getBuilderFieldValuesPath,
  getBuilderQueryPath,
  getBuilderWorkdayQueryPath,
} from './fabrikQueryPaths';
import { json } from './platformAuth';
import type {
  FabrikFieldOptions,
  FabrikFieldValuesPage,
  FabrikFieldValuesRequest,
  FabrikListQueryOptions,
  FabrikListRuntimeRow,
  FabrikSystemDataSource,
} from '../types/apps';

/**
 * Builds the JSON body the query route expects for a datasource page or row count. The source's
 * effective dates, or a Workday source's references, must already be resolved into fixed values.
 */
export const buildDataSourceQueryBody = (
  dataSource: FabrikSystemDataSource,
  options: FabrikListQueryOptions
) =>
  dataSource.type === 'workday'
    ? buildWorkdayQueryBody(dataSource, options)
    : 'count' in options
    ? {
        entity: dataSource.entity,
        ...(dataSource.filter !== undefined ? { filter: dataSource.filter } : {}),
        ...effectiveDateQueryFields(dataSource.effectiveDates),
        count: true as const,
      }
    : {
        entity: dataSource.entity,
        ...(dataSource.values !== undefined ? { values: dataSource.values } : {}),
        ...(dataSource.expand !== undefined ? { expand: dataSource.expand } : {}),
        ...(dataSource.filter !== undefined ? { filter: dataSource.filter } : {}),
        ...(dataSource.orderBy !== undefined ? { orderBy: dataSource.orderBy } : {}),
        ...effectiveDateQueryFields(dataSource.effectiveDates),
        top: options.top,
        skip: options.skip,
      };

/** Queries one page or only the row count for a Fabrik OData or Workday datasource. */
export const queryFabrikDataSource = (
  tenantId: string,
  systemId: string,
  dataSource: FabrikSystemDataSource,
  options: FabrikListQueryOptions,
  signal?: AbortSignal
) =>
  json<unknown>(
    dataSource.type === 'workday'
      ? getBuilderWorkdayQueryPath(systemId)
      : getBuilderQueryPath(systemId),
    tenantId,
    {
      method: 'POST',
      body: JSON.stringify(buildDataSourceQueryBody(dataSource, options)),
      signal,
    }
  );

/**
 * Reads, in the builder, the values some fields of an entity can take, with labels.
 * @param tenantId tenant of the builder
 * @param request system, entity and field names
 */
export const readFabrikFieldOptions = async (
  tenantId: string,
  { systemId, entity, fields }: { systemId: string; entity: string; fields: string[] }
): Promise<FabrikFieldOptions> =>
  (
    await json<{ fields: FabrikFieldOptions }>(getBuilderFieldOptionsPath(systemId), tenantId, {
      method: 'POST',
      body: JSON.stringify({ entity, fields }),
    })
  ).fields;

/**
 * Reads, in the builder, a search page of one system field's values, or the labels of some codes.
 * @param tenantId tenant of the builder
 * @param request system, entity, field, and either the search with its page or the codes
 */
export const readFabrikFieldValues = (
  tenantId: string,
  { systemId, ...body }: FabrikFieldValuesRequest
): Promise<FabrikFieldValuesPage> =>
  json<FabrikFieldValuesPage>(getBuilderFieldValuesPath(systemId), tenantId, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** Extracts rows from either an OData v2 (`d.results`) or v4 (`value`) payload. */
export const toODataRows = (response: unknown): FabrikListRuntimeRow[] => {
  if (!isRecord(response)) return [];
  if (isRecord(response.d) && Array.isArray(response.d.results)) {
    return response.d.results.filter(isRecord);
  }
  return Array.isArray(response.value) ? response.value.filter(isRecord) : [];
};

/** Reads the total row count from a count payload, or null when the API omits it. */
export const toODataCount = (response: unknown): number | null =>
  isRecord(response) && typeof response.count === 'number' ? response.count : null;
